'use client';

import { FC, useMemo, useState } from 'react';
import { SelectField } from '@tuic/web-compositions/shared/SelectField';
import { SwitchToggle } from '@tuic/web-compositions/core/SwitchToggle';
import { Form } from '@tuic/web-compositions/shared/Form';
import { useClientTranslation } from '@lib/i18n/client';
import { getTransportState } from '../utils';
import { getPhaserAttributesFromUrl, PhaserClientData, TransportState, setPhaserAttributesToUrl } from '@modules/phaser';
import { Token } from '@lib/token';
import { Locale } from '@tuic/i18n';
type Props = {
  data: PhaserClientData;
  token: Token;
  locale: Locale;
};
const defaultInitialState: TransportState = {
  bus: false,
  isDebugMode: false,
  isEmptyStateForced: false,
  trainVariant: 5,
  flightVariant: 0
};
export const TransportStateForm: FC<Props> = ({
  data,
  token,
  locale
}) => {
  const {
    t
  } = useClientTranslation();
  const [state, setState] = useState<TransportState>(() => {
    const attrs = getPhaserAttributesFromUrl(window.location.href);
    return {
      bus: attrs.bus ?? defaultInitialState.bus,
      isDebugMode: attrs.isDebugMode ?? defaultInitialState.isDebugMode,
      isEmptyStateForced: attrs.isEmptyStateForced ?? defaultInitialState.isEmptyStateForced,
      trainVariant: (attrs.trainVariant ?? defaultInitialState.trainVariant) as TransportState['trainVariant'],
      flightVariant: (attrs.flightVariant ?? defaultInitialState.flightVariant) as TransportState['flightVariant']
    };
  });
  const booking = JSON.parse(data.serializedPhasedBooking);
  const moreThanOneTraveller = booking.original?.travellers?.length;

  // Compute disabled states based on the current selection
  const disabledStates = useMemo(() => ({
    otherTransports: state.bus,
    bus: state.flightVariant !== 0 || state.trainVariant !== 5
  }), [state.bus, state.flightVariant, state.trainVariant]);
  const trainVariants = useMemo(() => [{
    value: '5',
    children: t('Variante 5 (Leer)')
  }, {
    value: '6',
    children: t('Variante 6 (Nicht buchbar)')
  }, {
    value: '2',
    children: t('Variante 2 (Buchbar)')
  }, {
    value: '3',
    children: t('Variante 3 (Bitte prüfen)')
  }, {
    value: '4',
    children: t('Variante 4 (Bitte neu buchen)')
  }], [t]);
  const flightVariants = useMemo(() => [{
    value: '0',
    children: t('Keine Flugsegmente')
  }, {
    value: '1',
    children: t('Ein Flugsegment | Alle Reisenden')
  }, {
    value: '2',
    children: t('Zwei Flugsegmente | Alle Reisenden')
  }, {
    value: '11',
    children: t('Ein Flugsegment | Pro Reisende(n)'),
    disabled: !moreThanOneTraveller
  }, {
    value: '22',
    children: t('Zwei Flugsegmente | Pro Reisende(n)'),
    disabled: !moreThanOneTraveller
  }], [moreThanOneTraveller, t]);
  const busOptions = useMemo(() => [{
    value: 'false',
    children: t('Kein Bus')
  }, {
    value: 'true',
    children: t('Bus')
  }], [t]);

  // Determine the current transport state
  const transportResult = useMemo(() => {
    return getTransportState({
      bus: state.bus || defaultInitialState.bus,
      trainVariant: state.trainVariant || defaultInitialState.trainVariant,
      flightVariant: state.flightVariant || defaultInitialState.flightVariant
    });
  }, [state]);

  // Handlers for form actions
  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    updateUrlAttributes(state);
  };
  const handleReset = (ev: React.FormEvent) => {
    ev.preventDefault();
    updateUrlAttributes({
      bus: undefined,
      trainVariant: undefined,
      flightVariant: undefined,
      isDebugMode: undefined,
      isEmptyStateForced: undefined
    });
  };
  return <div className="space-y-8" data-sentry-component="TransportStateForm" data-sentry-source-file="transit-state.tsx">
      <Form header={{
      headline: t('Anreise- und Abreise Einstellungen'),
      subline: t('Nutzen Sie das Transport-Einstellungsformular, um Anreise- und Abreisedaten für verschiedene Reisemethoden (Bahn/Zug, Flug und Bus) zu simulieren'),
      facts: [{
        icon: 'check',
        text: t('Wenn Sie eine Zug- oder Flugoption wählen, wird Bus deaktiviert.')
      }, {
        icon: 'check',
        text: t('Wenn Sie Bus auswählen, werden die Optionen für Zug und Flug deaktiviert.')
      }]
    }} cancelButton={{
      children: t('Zurücksetzen'),
      onClick: handleReset
    }} submitButton={{
      children: t('Anwenden'),
      onClick: handleSubmit
    }} data-sentry-element="Form" data-sentry-source-file="transit-state.tsx">
        {/* Train */}
        <div className="space-y-2">
          <SelectField label={t('Zug-Variante')} note={t('Wählen Sie das gewünschte Szenario aus (z.B. leer, buchbar, benötigt Überprüfung usw.).')} value={state.trainVariant?.toString()} onChange={event => {
          const value = event.target.value;
          if (state.trainVariant === 5 && value !== '5' && state.bus) {
            setState({
              ...state,
              trainVariant: Number(value) as TransportState['trainVariant'],
              bus: false
            });
          } else {
            setState({
              ...state,
              trainVariant: Number(value) as TransportState['trainVariant']
            });
          }
        }} disabled={disabledStates.otherTransports} options={trainVariants} data-sentry-element="SelectField" data-sentry-source-file="transit-state.tsx" />
        </div>
        {/* Flight */}
        <div className="space-y-2">
          <SelectField label={t('Flug-Segmente')} note={t('Wählen Sie die Anzahl der Flugabschnitte aus (z.B. kein Flug, ein Flugabschnitt, zwei Flugabschnitte).')} value={state.flightVariant?.toString()} onChange={event => {
          const value = event.target.value;
          if (state.flightVariant === 0 && value !== '0' && state.bus) {
            setState({
              ...state,
              flightVariant: Number(value) as TransportState['flightVariant'],
              bus: false
            });
          } else {
            setState({
              ...state,
              flightVariant: Number(value) as TransportState['flightVariant']
            });
          }
        }} disabled={disabledStates.otherTransports} options={flightVariants} data-sentry-element="SelectField" data-sentry-source-file="transit-state.tsx" />
        </div>
        {/* Bus */}
        <div className="space-y-2">
          <SelectField label={t('Bus-Daten')} note={t('Bus als Reisemethode aktivieren oder deaktivieren.')} value={state.bus ? 'true' : 'false'} onChange={event => {
          const checked = event.target.value === 'true';
          setState({
            ...state,
            bus: checked,
            trainVariant: checked ? 5 : state.trainVariant,
            flightVariant: checked ? 0 : state.flightVariant
          });
        }} disabled={disabledStates.bus} options={busOptions} data-sentry-element="SelectField" data-sentry-source-file="transit-state.tsx" />
        </div>
        {/* Expected Transport State Result Display */}
        <div className="space-y-2 text-marine-high-emphasis">
          <span>{t('Aktueller Transportstatus: ')}</span>
          <span>{transportResult.text}</span>
        </div>
        {/* Debug Mode */}
        <div className="items-center flex gap-4">
          <span className="text-marine-high-emphasis">
            {t('Entwickler Modus')}
          </span>
          <SwitchToggle checked={state.isDebugMode} onCheckedChange={checked => {
          setState({
            ...state,
            isDebugMode: checked
          });
        }} data-sentry-element="SwitchToggle" data-sentry-source-file="transit-state.tsx" />
        </div>
        {/* Empty State force */}
        <div className="items-center flex gap-4">
          <span className="text-marine-high-emphasis">
            {t('Empty State erzwingen')}
          </span>
          <SwitchToggle checked={state.isEmptyStateForced} onCheckedChange={checked => {
          setState({
            ...state,
            isEmptyStateForced: checked
          });
        }} data-sentry-element="SwitchToggle" data-sentry-source-file="transit-state.tsx" />
        </div>
      </Form>
    </div>;
};
function updateUrlAttributes(state: TransportState) {
  const attrs = getPhaserAttributesFromUrl(window.location.href);
  let url = setPhaserAttributesToUrl(window.location.href, {
    ...attrs,
    bus: state.bus,
    trainVariant: state.trainVariant,
    flightVariant: state.flightVariant,
    isDebugMode: state.isDebugMode,
    isEmptyStateForced: state.isEmptyStateForced
  });
  window.location.href = url.toString();
}